import { DateTime } from 'luxon';
import { GetRemediationActivityGranularityEnum } from '../../api/schema';

// Shared by the period bar charts (Daily Warehouse Activity, Average
// Daily Jobs Started): which periods are shown, how long each one is, the
// trailing average, the y axis and where the hover tooltip goes. The charts
// themselves only decide what a bar is made of.

export type ActivityGranularity = GetRemediationActivityGranularityEnum;

// How many periods each granularity shows: a week of days, a quarter of weeks,
// a year of months.
export const ACTIVITY_PERIODS: Record<ActivityGranularity, number> = { day: 7, week: 13, month: 12 };

// The trend is a trailing three-period average, so two periods before the
// first visible one are fetched too; they feed the average and are not drawn.
export const TREND_PERIODS = 3;
export const ACTIVITY_LEAD_PERIODS = TREND_PERIODS - 1;

// Room around the plot for the y-axis labels (left) and x-axis labels (bottom).
export const AXIS = { left: 34, bottom: 26, top: 8, right: 4 };

export const TOOLTIP_GAP = 12;

const unitOf = { day: 'days', week: 'weeks', month: 'months' } as const;

export type PeriodSpan = {
  // First day, YYYY-MM-DD: the key the server buckets rows under.
  iso: string;
  label: string;
  title: string;
  // Days the period covers, or for the current one the days elapsed so far.
  days: number;
  partial: boolean;
};

// Every period ending with the current one, activity or not, in the viewer's
// zone. A week is Monday-start, as the server buckets it. The current week or
// month is partial and counts only the days elapsed, so a rate over it does
// not read as a drop.
export const buildPeriodSpans = (granularity: ActivityGranularity, total: number): PeriodSpan[] => {
  const today = DateTime.local().startOf('day');
  const current = today.startOf(granularity);
  return Array.from({ length: total }, (_, i) => {
    const start = current.minus({ [unitOf[granularity]]: total - 1 - i });
    const partial = i === total - 1 && granularity !== 'day';
    const end = partial ? today.plus({ days: 1 }) : start.plus({ [unitOf[granularity]]: 1 });
    const days = Math.max(1, Math.round(end.diff(start, 'days').days));
    const label = granularity === 'day' ? start.toFormat('EEE d') : granularity === 'week' ? start.toFormat('MMM d') : start.toFormat('MMM');
    const title =
      granularity === 'day' ? start.toFormat('EEEE, MMM d') : granularity === 'week' ? `Week of ${start.toFormat('MMM d')}` : start.toFormat('MMMM yyyy');
    return { iso: start.toISODate() as string, label, title, days, partial };
  });
};

// Trailing average at period i over it and the two before it, weighted by
// days so a short month or the partial current period counts for what it is:
// the window's total over the window's days. Undefined for the lead periods.
export const trailingRate = (totals: number[], spans: PeriodSpan[], i: number): number | undefined => {
  if (i < ACTIVITY_LEAD_PERIODS) return undefined;
  let sum = 0;
  let days = 0;
  for (let k = i - ACTIVITY_LEAD_PERIODS; k <= i; k++) {
    sum += totals[k];
    days += spans[k].days;
  }
  return sum / days;
};

// A readable step for about four gridlines over [0, max]: 1, 2, 2.5 or 5
// times a power of ten. Whole-number axes (a single day's counts) step by
// whole numbers too.
const niceStep = (max: number, wholeNumbers: boolean): number => {
  const raw = Math.max(max, 0.0001) / 4;
  const magnitude = 10 ** Math.floor(Math.log10(raw));
  const norm = raw / magnitude;
  const step = (norm <= 1 ? 1 : norm <= 2 ? 2 : norm <= 2.5 ? 2.5 : norm <= 5 ? 5 : 10) * magnitude;
  return wholeNumbers ? Math.max(1, Math.ceil(step)) : step;
};

// Headroom: the domain is the next clean step above the tallest value, so
// nothing touches the frame.
export const yScale = (tallest: number, wholeNumbers: boolean): { max: number; step: number; ticks: number[] } => {
  const step = niceStep(tallest, wholeNumbers);
  const max = Math.max(step * 2, Math.ceil((tallest * 1.05) / step) * step);
  const ticks: number[] = [];
  for (let i = 0; i * step <= max + 1e-9; i++) ticks.push(i * step);
  return { max, step, ticks };
};

export const formatTick = (value: number, step: number): string => (step >= 1 ? String(Math.round(value * 10) / 10) : value.toFixed(step >= 0.1 ? 1 : 2));

// Tooltip placement: beside the hovered period, never over its bars. It goes
// right of the period when it fits, else left, else on the roomier side flush
// against the period, running into the axis gutter or past the plot rather
// than being pushed back over the bars. The math is in plot coordinates; the
// tooltip lives in the padded outer box, so the axis padding is added on the
// way out.
export const placeTooltip = (
  plot: { width: number; height: number },
  period: number,
  periodCount: number,
  cursorY: number,
  tip: { w: number; h: number },
): { left: number; top: number } => {
  const periodWidth = plot.width / Math.max(1, periodCount);
  const periodLeft = period * periodWidth;
  const periodRight = (period + 1) * periodWidth;
  const minLeft = -AXIS.left;
  const maxRight = plot.width + AXIS.right;
  const right = periodRight + TOOLTIP_GAP;
  const left = periodLeft - TOOLTIP_GAP - tip.w;
  let x: number;
  if (right + tip.w <= maxRight) x = right;
  else if (left >= minLeft) x = left;
  else x = maxRight - periodRight >= periodLeft - minLeft ? right : left;
  let top = cursorY - TOOLTIP_GAP - tip.h;
  if (top < 0) top = cursorY + TOOLTIP_GAP;
  top = Math.max(0, Math.min(top, plot.height - tip.h));
  return { left: x + AXIS.left, top: top + AXIS.top };
};
